import type { ApiResponse } from '../../types'
import { client, unwrap, apiError, CotApiError } from './client'

// Server-held acting session + guardian PIN (COTC_GroupsModelRevision R11-R13). The acting person
// only changes through /profile/switch; switching up needs the family PIN. No other request carries
// an acting-person id — the whole point of the fix.

export interface ActingSessionStatus {
  actingPersonId: string
  isGuardianActing: boolean
  /** Family has managed profiles and no session established — the SPA must force a choice (R13). */
  choiceRequired: boolean
  pinSet: boolean
}

/** GET /profile/session — current acting-session state (never forces a choice). */
export async function getActingSession(): Promise<ActingSessionStatus> {
  try {
    const res = await client.get<ApiResponse<ActingSessionStatus>>('/profile/session')
    return unwrap(res.data)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/**
 * POST /profile/switch — point the session at a profile. Switching to a guardian/holder requires
 * the PIN; a {@link CotApiError} whose message is `pin_required` / `pin_invalid` / `pin_not_set` /
 * `pin_rate_limited` tells the caller to prompt for (or re-prompt) the PIN.
 */
export async function switchProfile(personId: string, pin?: string): Promise<ActingSessionStatus> {
  try {
    const res = await client.post<ApiResponse<ActingSessionStatus>>('/profile/switch', { personId, pin: pin ?? null })
    return unwrap(res.data)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** POST /profile/pin — set or change the family guardian PIN. */
export async function setGuardianPin(newPin: string, currentPin?: string): Promise<void> {
  try {
    const res = await client.post<ApiResponse>('/profile/pin', { newPin, currentPin: currentPin ?? null })
    unwrap(res.data)
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}
